import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useChatSessions from './useChatSessions';
import { interactiveServiceFetch } from '../../services/interactiveServiceFetch';
import {
    CONTEXT_PROD_MODE,
    ERROR_TYPES,
    EVENT_TYPES,
    INTERACTIVE_SERVICE_BASE_URL,
    MESSAGE_ROLES,
    SESSION_TITLE_PLACEHOLDER_MAX_LENGTH,
    STEP_STATUS
} from '../constants';

// Sentinel key used in the per-session maps for the "new chat" view (no real session id yet).
export const NEW_CHAT_KEY = '__new_chat__';

const createMessageId = () => `${MESSAGE_ROLES.ASSISTANT}-${Date.now()}`;

export const SSE_COMPLETION_EVENTS = [
    EVENT_TYPES.COMPLETED,
    EVENT_TYPES.ERROR,
    EVENT_TYPES.USER_ERROR,
    EVENT_TYPES.CANCELLED,
    EVENT_TYPES.FINISH,
];

const createAssistantMessage = (messageId, runId = null) => ({
    id: messageId,
    runId,
    role: MESSAGE_ROLES.ASSISTANT,
    content: [],
    isThinking: true,
    isStreaming: false,
    createdAt: new Date(),
    startedAt: Date.now(),
    finishedAt: null,
    steps: [],
    ephemeralUpdates: [],
    reasoning: [],
});

const updateMessageSteps = (existingSteps, newStep) => {
    const stepIndex = existingSteps.findIndex(s => s.step_id === newStep.step_id);
    const updatedSteps =
        stepIndex >= 0
            ? existingSteps.map((step, idx) => (idx === stepIndex ? { ...step, ...newStep } : step))
            : [...existingSteps, newStep];

    return {
        steps: updatedSteps,
        isThinking: updatedSteps.some(s => s.status === STEP_STATUS.RUNNING),
    };
};

const sessionKeyFor = sessionId => sessionId ?? NEW_CHAT_KEY;

const TOOL_EXECUTION_FAILED = 'Tool execution failed';

const extractToolError = ({ output, isError, errorText, error } = {}) => {
    if (output?.status === 'error') return output.error || TOOL_EXECUTION_FAILED;
    if (output?.is_error || output?.isError) return output.error || output.errorText || TOOL_EXECUTION_FAILED;
    if (isError) return errorText || error || TOOL_EXECUTION_FAILED;
    return null;
};

const useChat = ({ runPath = 'runs', sessionType, onFinish, loadSessionsOnMount = true, contextEnv = CONTEXT_PROD_MODE } = {}) => {
    const api = INTERACTIVE_SERVICE_BASE_URL;

    // Per-session message arrays, keyed by sessionId (or NEW_CHAT_KEY for the pending new-chat bucket).
    const [sessionMessages, setSessionMessages] = useState({});
    // Active runs per session key. Each entry is { runId, streamingMessageId }.
    const [activeRuns, setActiveRuns] = useState({});
    // Per-session error messages.
    const [sessionErrors, setSessionErrors] = useState({});
    const [stopError, setStopError] = useState(null);
    // Per-session pending input text, keyed the same way as sessionMessages.
    const [sessionInputs, setSessionInputs] = useState({});

    const onFinishRef = useRef(onFinish);
    const currentKeyRef = useRef(NEW_CHAT_KEY);
    const sessionMessagesRef = useRef({});
    const sessionInputsRef = useRef({});
    const activeRunsRef = useRef({});

    const setInput = useCallback(value => {
        setSessionInputs(prev => {
            const key = currentKeyRef.current;
            const current = prev[key] ?? '';
            const next = typeof value === 'function' ? value(current) : value;
            if (next === current) return prev;
            return { ...prev, [key]: next };
        });
    }, []);

    useEffect(() => {
        sessionMessagesRef.current = sessionMessages;
    }, [sessionMessages]);

    useEffect(() => {
        sessionInputsRef.current = sessionInputs;
    }, [sessionInputs]);

    useEffect(() => {
        activeRunsRef.current = activeRuns;
    }, [activeRuns]);

    useEffect(() => {
        onFinishRef.current = onFinish;
    }, [onFinish]);

    const updateSessionMessages = useCallback((key, updater) => {
        setSessionMessages(prev => {
            const current = prev[key] ?? [];
            const next = typeof updater === 'function' ? updater(current) : updater;
            return { ...prev, [key]: next };
        });
    }, []);

    const setStreamingMessageIdFor = useCallback((key, messageId) => {
        setActiveRuns(prev => {
            const existing = prev[key];
            if (!existing) return prev;
            return { ...prev, [key]: { ...existing, streamingMessageId: messageId } };
        });
    }, []);

    const removeActiveRun = useCallback(key => {
        setActiveRuns(prev => {
            if (!(key in prev)) return prev;
            const { [key]: _, ...rest } = prev;
            return rest;
        });
    }, []);

    const purgeSessionState = useCallback(sessionId => {
        const key = sessionKeyFor(sessionId);
        const purgeMap = prev => {
            if (!(key in prev)) return prev;
            const { [key]: _, ...rest } = prev;
            return rest;
        };
        setSessionMessages(purgeMap);
        setSessionInputs(purgeMap);
        setSessionErrors(purgeMap);
    }, []);

    const onSessionChange = useCallback((sessionId, shouldClearMessages, loadedMessages = null, sessionError = null, activeRunId = null) => {
        const key = sessionKeyFor(sessionId);
        if (shouldClearMessages) {
            // Only clear if no active run is in flight for this key.
            if (!activeRunsRef.current[key]) {
                updateSessionMessages(key, []);
            }
        } else if (loadedMessages) {
            // Don't overwrite messages while a stream is updating them; the in-memory copy is more current.
            if (!activeRunsRef.current[key]) {
                updateSessionMessages(key, loadedMessages);
            }
        }
        setSessionErrors(prev => ({ ...prev, [key]: sessionError ?? null }));
        setStopError(null);
        // If the backend reports an active run for this session, attach to it so streaming resumes after refresh.
        if (activeRunId && !activeRunsRef.current[key]) {
            setActiveRuns(prev => ({ ...prev, [key]: { runId: activeRunId, streamingMessageId: null } }));
        }
    }, [updateSessionMessages]);

    const {
        currentSessionId,
        setCurrentSessionId,
        sessions,
        isLoadingSessions,
        sessionsLoadError,
        loadingSessionId,
        sessionDeletedNotice,
        sessionDeleteError,
        sessionLoadError,
        fetchSessions,
        upsertSession,
        loadSession,
        deleteSession,
        updateSessionTitle,
    } = useChatSessions({ api, sessionType, onSessionChange, onSessionRemoved: purgeSessionState, loadSessionsOnMount });

    const currentKey = sessionKeyFor(currentSessionId);
    currentKeyRef.current = currentKey;
    const messages = sessionMessages[currentKey] ?? [];
    const isLoading = !!activeRuns[currentKey];
    const activeRunId = activeRuns[currentKey]?.runId ?? null;
    const error = sessionErrors[currentKey] ?? null;
    const input = sessionInputs[currentKey] ?? '';

    // When the session list reports active runs we don't yet know about, attach to them.
    // Covers the case of returning to the page while a stream from a previous visit is still going.
    useEffect(() => {
        const additions = {};
        for (const session of sessions || []) {
            const runId = session?.active_run_id;
            const sid = session?.session_id;
            if (runId && sid && !activeRunsRef.current[sid]) {
                additions[sid] = { runId, streamingMessageId: null };
            }
        }
        if (Object.keys(additions).length === 0) return;
        setActiveRuns(prev => ({ ...prev, ...additions }));
    }, [sessions]);

    const streamingSessionIds = useMemo(
        () => Object.keys(activeRuns).filter(k => k !== NEW_CHAT_KEY),
        [activeRuns]
    );

    const streamDescriptors = useMemo(
        () => Object.entries(activeRuns).map(([key, { runId }]) => ({ key, runId })),
        [activeRuns]
    );

    const clearCurrentSession = useCallback(() => {
        setCurrentSessionId(null);
        // Only clear the new-chat bucket; preserve other sessions' state (including any in-flight streams).
        if (!activeRunsRef.current[NEW_CHAT_KEY]) {
            updateSessionMessages(NEW_CHAT_KEY, []);
        }
        setSessionErrors(prev => ({ ...prev, [NEW_CHAT_KEY]: null }));
        setSessionInputs(prev => {
            if (!(NEW_CHAT_KEY in prev)) return prev;
            const { [NEW_CHAT_KEY]: _, ...rest } = prev;
            return rest;
        });
    }, [setCurrentSessionId, updateSessionMessages]);

    const setErrorFor = useCallback((key, value) => {
        setSessionErrors(prev => ({ ...prev, [key]: value }));
    }, []);

    const handleError = useCallback((key, errorMessage, isUserError = false) => {
        const messageId = activeRunsRef.current[key]?.streamingMessageId;
        removeActiveRun(key);

        if (!isUserError) {
            setErrorFor(key, errorMessage);
            if (!messageId) return;
            updateSessionMessages(key, prev =>
                prev.map(msg => {
                    if (msg.id !== messageId) return msg;
                    const stoppedSteps = (msg.steps || []).map(step =>
                        step.status === STEP_STATUS.RUNNING ? { ...step, status: STEP_STATUS.FAILED } : step
                    );
                    const stoppedEphemerals = (msg.ephemeralUpdates || []).map(update =>
                        update.isStreaming ? { ...update, isStreaming: false } : update
                    );
                    return {
                        ...msg,
                        isThinking: false,
                        isStreaming: false,
                        steps: stoppedSteps,
                        ephemeralUpdates: stoppedEphemerals,
                        finishedAt: Date.now(),
                    };
                })
            );
            return;
        }

        setErrorFor(key, null);

        const textContent = [{ type: 'error', data: errorMessage }];
        if (!messageId) {
            updateSessionMessages(key, prev => [
                ...prev,
                { ...createAssistantMessage(createMessageId()), isThinking: false, content: textContent },
            ]);
        } else {
            updateSessionMessages(key, prev =>
                prev.map(msg =>
                    msg.id === messageId
                        ? { ...msg, isThinking: false, isStreaming: false, steps: [], content: [...(msg.content || []), ...textContent] }
                        : msg
                )
            );
        }
    }, [removeActiveRun, setErrorFor, updateSessionMessages]);

    const handleStarted = useCallback(key => {
        const messageId = createMessageId();
        const runId = activeRunsRef.current[key]?.runId ?? null;
        setStreamingMessageIdFor(key, messageId);
        updateSessionMessages(key, prev => [...prev, createAssistantMessage(messageId, runId)]);
    }, [setStreamingMessageIdFor, updateSessionMessages]);

    const handleCompleted = useCallback(key => {
        const messageId = activeRunsRef.current[key]?.streamingMessageId;
        let finishedMessage = null;
        updateSessionMessages(key, prev => {
            const updated = prev.map(msg => {
                if (msg.id !== messageId) return msg;

                const completedSteps = (msg.steps || []).map(step =>
                    step.status !== STEP_STATUS.FAILED ? { ...step, status: STEP_STATUS.COMPLETED } : step
                );
                return {
                    ...msg,
                    isThinking: false,
                    isLoading: false,
                    isStreaming: false,
                    steps: completedSteps,
                    finishedAt: Date.now(),
                };
            });
            finishedMessage = updated.find(m => m.id === messageId) || null;
            return updated;
        });
        if (finishedMessage) onFinishRef.current?.(finishedMessage);
        removeActiveRun(key);
    }, [removeActiveRun, updateSessionMessages]);

    const handleCancelled = useCallback((key, message) => {
        const messageId = activeRunsRef.current[key]?.streamingMessageId;
        removeActiveRun(key);
        if (!messageId) return;

        const displayedMessage = message?.message ?? '**Response was interrupted**';
        updateSessionMessages(key, prev =>
            prev.map(msg => {
                if (msg.id !== messageId) return msg;
                const stoppedSteps = (msg.steps || []).map(step =>
                    step.status === STEP_STATUS.RUNNING ? { ...step, status: STEP_STATUS.FAILED } : step
                );
                const stoppedEphemerals = (msg.ephemeralUpdates || []).map(update =>
                    update.isStreaming ? { ...update, isStreaming: false } : update
                );
                return {
                    ...msg,
                    isThinking: false,
                    isStreaming: false,
                    steps: stoppedSteps,
                    ephemeralUpdates: stoppedEphemerals,
                    finishedAt: Date.now(),
                    content: Array.isArray(msg.content) && msg.content.length
                        ? [...msg.content, { type: 'markdown', data: `\n\n${displayedMessage}` }]
                        : [{ type: 'markdown', data: displayedMessage }],
                };
            })
        );
    }, [removeActiveRun, updateSessionMessages]);

    const handleToken = useCallback((key, message) => {
        const messageId = activeRunsRef.current[key]?.streamingMessageId;
        const content = message.content || message.token || '';
        if (!messageId || !content) return;

        updateSessionMessages(key, prev =>
            prev.map(msg => {
                if (msg.id !== messageId) return msg;

                const updatedContent = Array.isArray(msg.content) ? [...msg.content] : [];
                const lastBlock = updatedContent[updatedContent.length - 1];
                if (lastBlock && lastBlock.type === 'markdown') {
                    lastBlock.data += content;
                } else {
                    updatedContent.push({ type: 'markdown', data: content });
                }

                return { ...msg, content: updatedContent, isThinking: false, isStreaming: true };
            })
        );
    }, [updateSessionMessages]);

    const handleStepUpdate = useCallback((key, message) => {
        const messageId = activeRunsRef.current[key]?.streamingMessageId;
        const { step } = message;
        if (!step || !messageId) return;

        updateSessionMessages(key, prev =>
            prev.map(msg =>
                msg.id === messageId
                    ? { ...msg, ...updateMessageSteps(msg.steps || [], step) }
                    : msg
            )
        );
    }, [updateSessionMessages]);

    const handleArtifact = useCallback((key, message) => {
        const messageId = activeRunsRef.current[key]?.streamingMessageId;
        const { artifact } = message;
        if (!artifact || !messageId) return;

        updateSessionMessages(key, prev =>
            prev.map(msg => {
                if (msg.id !== messageId) return msg;
                const updatedContent = Array.isArray(msg.content) ? [...msg.content] : [];
                updatedContent.push(artifact);
                return { ...msg, content: updatedContent };
            })
        );
    }, [updateSessionMessages]);

    const upsertEphemeralUpdate = useCallback((key, updateId, mutator) => {
        const messageId = activeRunsRef.current[key]?.streamingMessageId;
        if (!messageId) return;

        updateSessionMessages(key, prev =>
            prev.map(msg => {
                if (msg.id !== messageId) return msg;

                const existing = msg.ephemeralUpdates || [];
                const idx = existing.findIndex(u => u.id === updateId);
                const current = idx >= 0 ? existing[idx] : { id: updateId, text: '', isStreaming: true };
                const next = mutator(current);
                const updated = idx >= 0
                    ? existing.map((u, i) => (i === idx ? next : u))
                    : [...existing, next];
                return { ...msg, ephemeralUpdates: updated, isThinking: false };
            })
        );
    }, [updateSessionMessages]);

    const handleEphemeralUpdateStart = useCallback((key, message) => {
        upsertEphemeralUpdate(key, message.id, current => ({
            ...current,
            title: message.title || current.title || '',
            isStreaming: true,
        }));
    }, [upsertEphemeralUpdate]);

    const handleEphemeralUpdateDelta = useCallback((key, message) => {
        upsertEphemeralUpdate(key, message.id, current => ({
            ...current,
            text: (current.text || '') + (message.delta || ''),
            isStreaming: true,
        }));
    }, [upsertEphemeralUpdate]);

    const handleEphemeralUpdateEnd = useCallback((key, message) => {
        upsertEphemeralUpdate(key, message.id, current => ({ ...current, isStreaming: false }));
    }, [upsertEphemeralUpdate]);

    const upsertReasoning = useCallback((key, reasoningId, mutator) => {
        const messageId = activeRunsRef.current[key]?.streamingMessageId;
        if (!messageId) return;

        updateSessionMessages(key, prev =>
            prev.map(msg => {
                if (msg.id !== messageId) return msg;

                const existing = msg.reasoning || [];
                const idx = existing.findIndex(r => r.id === reasoningId);
                const current = idx >= 0 ? existing[idx] : { id: reasoningId, text: '', isStreaming: true };
                const next = mutator(current);
                const updated = idx >= 0
                    ? existing.map((r, i) => (i === idx ? next : r))
                    : [...existing, next];
                return { ...msg, reasoning: updated };
            })
        );
    }, [updateSessionMessages]);

    const handleReasoningStart = useCallback((key, message) => {
        upsertReasoning(key, message.id, current => ({ ...current, isStreaming: true }));
    }, [upsertReasoning]);

    const handleReasoningDelta = useCallback((key, message) => {
        upsertReasoning(key, message.id, current => ({
            ...current,
            text: (current.text || '') + (message.delta || ''),
            isStreaming: true,
        }));
    }, [upsertReasoning]);

    const handleReasoningEnd = useCallback((key, message) => {
        upsertReasoning(key, message.id, current => ({ ...current, isStreaming: false }));
    }, [upsertReasoning]);

    const handleResponseComplete = useCallback(key => {
        const messageId = activeRunsRef.current[key]?.streamingMessageId;
        if (!messageId) return;

        updateSessionMessages(key, prev =>
            prev.map(msg => {
                if (msg.id !== messageId) return msg;

                const updatedContent = Array.isArray(msg.content) ? [...msg.content] : [];
                const lastBlock = updatedContent[updatedContent.length - 1];
                if (lastBlock && lastBlock.type === 'markdown') {
                    lastBlock.data += '\n\n';
                }

                return { ...msg, isStreaming: false, content: updatedContent };
            })
        );
    }, [updateSessionMessages]);

    const handleFinish = useCallback((key, message) => {
        const reason = message.finishReason;
        if (reason === 'cancelled') return handleCancelled(key, message);
        if (reason === 'error') return handleError(key, message.errorText || 'An error occurred', true);
        return handleCompleted(key);
    }, [handleCompleted, handleCancelled, handleError]);

    const handleToolInputStart = useCallback((key, message) => {
        const step = {
            step_id: message.toolCallId,
            name: message.toolName,
            title: message.toolLabel || message.toolName,
            status: STEP_STATUS.RUNNING,
        };
        handleStepUpdate(key, { step });
    }, [handleStepUpdate]);

    const handleToolOutputAvailable = useCallback((key, message) => {
        const errorReason = extractToolError(message);
        const step = errorReason
            ? {
                step_id: message.toolCallId,
                status: STEP_STATUS.FAILED,
                errorReason,
            }
            : {
                step_id: message.toolCallId,
                status: STEP_STATUS.COMPLETED,
            };
        handleStepUpdate(key, { step });
    }, [handleStepUpdate]);

    const handleDataEvent = useCallback((key, message) => {
        const dataType = message.type.slice(EVENT_TYPES.DATA_PREFIX.length);
        if (dataType === 'artifact' && message.data) {
            const artifactData = message.data;
            const artifact = {
                type: artifactData.artifact_type || dataType,
                data: artifactData.data,
                title: artifactData.title,
                insights: artifactData.insights,
                is_final_analysis: artifactData.is_final_analysis,
                metadata: artifactData.metadata,
            };
            handleArtifact(key, { artifact });
        }
    }, [handleArtifact]);

    const handleStreamMessage = useCallback((key, message) => {
        const { type } = message;

        // Lifecycle events always dispatch.
        if (type === EVENT_TYPES.STARTED || type === EVENT_TYPES.START) return handleStarted(key);
        if (type === EVENT_TYPES.COMPLETED) return handleCompleted(key);
        if (type === EVENT_TYPES.CANCELLED) return handleCancelled(key, message);
        if (type === EVENT_TYPES.RESPONSE_COMPLETE) return handleResponseComplete(key);
        if (type === EVENT_TYPES.SESSION_TITLE_UPDATED) return upsertSession(message.session_id, message.title);
        if (type === EVENT_TYPES.FINISH) return handleFinish(key, message);
        if (type === EVENT_TYPES.STREAM_ERROR) return handleError(key, message.errorText || 'An error occurred', true);

        if (Object.values(ERROR_TYPES).includes(type)) {
            return handleError(key, message.message || message.error || 'An error occurred', type === ERROR_TYPES.USER_ERROR);
        }

        // Content events only dispatch while a streaming message exists.
        if (!activeRunsRef.current[key]?.streamingMessageId) return;

        if (type === EVENT_TYPES.TOKEN || type === EVENT_TYPES.TOKEN_BATCH) return handleToken(key, message);
        if (type === EVENT_TYPES.TEXT_DELTA) return handleToken(key, { content: message.delta });
        if (type === EVENT_TYPES.TEXT_END) return handleResponseComplete(key);
        if (type === EVENT_TYPES.REASONING_START) return handleReasoningStart(key, message);
        if (type === EVENT_TYPES.REASONING_DELTA) return handleReasoningDelta(key, message);
        if (type === EVENT_TYPES.REASONING_END) return handleReasoningEnd(key, message);
        if (type === EVENT_TYPES.STEP_UPDATE) return handleStepUpdate(key, message);
        if (type === EVENT_TYPES.ARTIFACT) return handleArtifact(key, message);
        if (type === EVENT_TYPES.TOOL_INPUT_START) return handleToolInputStart(key, message);
        if (type === EVENT_TYPES.TOOL_OUTPUT_AVAILABLE) return handleToolOutputAvailable(key, message);
        if (type === EVENT_TYPES.EPHEMERAL_UPDATE_START) return handleEphemeralUpdateStart(key, message);
        if (type === EVENT_TYPES.EPHEMERAL_UPDATE_DELTA) return handleEphemeralUpdateDelta(key, message);
        if (type === EVENT_TYPES.EPHEMERAL_UPDATE_END) return handleEphemeralUpdateEnd(key, message);
        if (type?.startsWith(EVENT_TYPES.DATA_PREFIX)) return handleDataEvent(key, message);
    }, [
        handleStarted,
        handleCompleted,
        handleCancelled,
        handleFinish,
        handleError,
        handleResponseComplete,
        handleToken,
        handleStepUpdate,
        handleArtifact,
        handleToolInputStart,
        handleToolOutputAvailable,
        handleEphemeralUpdateStart,
        handleEphemeralUpdateDelta,
        handleEphemeralUpdateEnd,
        handleReasoningStart,
        handleReasoningDelta,
        handleReasoningEnd,
        handleDataEvent,
        upsertSession,
    ]);

    const handleStreamError = useCallback((key, errorMessage) => {
        handleError(key, errorMessage);
    }, [handleError]);

    const handleSubmit = useCallback(
        async (e, options = {}) => {
            e?.preventDefault();

            const submissionKey = sessionKeyFor(currentSessionId);
            const rawContent = options?.content || sessionInputsRef.current[submissionKey] || '';
            const messageContent = rawContent.trim();
            if (!messageContent) return;

            // Block a second submission to the same view while it has an active run.
            if (activeRunsRef.current[submissionKey]) return;

            setStopError(null);
            setErrorFor(submissionKey, null);

            const userMessage = {
                id: `${MESSAGE_ROLES.USER}-${Date.now()}`,
                role: MESSAGE_ROLES.USER,
                content: [{ type: 'markdown', data: messageContent }],
                createdAt: new Date(),
            };

            updateSessionMessages(submissionKey, prev => [...prev, userMessage]);
            setSessionInputs(prev => {
                if (!(submissionKey in prev)) return prev;
                const { [submissionKey]: _, ...rest } = prev;
                return rest;
            });

            const endpoint = `${api}/${runPath}`;
            const requestBody = options?.body || {
                user_query: messageContent,
                request_id: `req-${Date.now()}`,
                session_id: currentSessionId,
                context_env: contextEnv,
            };

            try {
                const response = await interactiveServiceFetch(endpoint, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(requestBody),
                });

                if (!response.ok) {
                    throw new Error(`Failed to create run: ${response.status} ${response.statusText}`);
                }

                const result = await response.json();
                const runId = result.run_id;
                const newSessionId = result.session_id || currentSessionId;
                const finalKey = sessionKeyFor(newSessionId);

                // If we submitted from the new-chat bucket and got back a real session id, migrate state.
                if (submissionKey === NEW_CHAT_KEY && finalKey !== NEW_CHAT_KEY) {
                    setSessionMessages(prev => {
                        if (!(NEW_CHAT_KEY in prev)) return prev;
                        const { [NEW_CHAT_KEY]: pendingMessages, ...rest } = prev;
                        return { ...rest, [finalKey]: pendingMessages };
                    });
                    setSessionErrors(prev => {
                        const { [NEW_CHAT_KEY]: _, ...rest } = prev;
                        return { ...rest, [finalKey]: null };
                    });
                    // Carry over any text typed in the new-chat input between submit and response.
                    setSessionInputs(prev => {
                        if (!(NEW_CHAT_KEY in prev)) return prev;
                        const { [NEW_CHAT_KEY]: pendingInput, ...rest } = prev;
                        return pendingInput ? { ...rest, [finalKey]: pendingInput } : rest;
                    });
                }

                setActiveRuns(prev => ({
                    ...prev,
                    [finalKey]: { runId, streamingMessageId: null },
                }));

                setCurrentSessionId(newSessionId);

                if (newSessionId && newSessionId !== currentSessionId) {
                    const placeholderTitle = messageContent.length > SESSION_TITLE_PLACEHOLDER_MAX_LENGTH
                        ? `${messageContent.slice(0, SESSION_TITLE_PLACEHOLDER_MAX_LENGTH)}...`
                        : messageContent;
                    upsertSession(newSessionId, placeholderTitle);
                }
            } catch (err) {
                handleError(submissionKey, err.message || 'An error occurred');
            }
        },
        [api, runPath, currentSessionId, setCurrentSessionId, upsertSession, handleError, contextEnv, updateSessionMessages, setErrorFor]
    );

    const append = useCallback(
        async message => {
            await handleSubmit(null, { content: message.content });
        },
        [handleSubmit]
    );

    const reload = useCallback(async () => {
        const currentMessages = sessionMessagesRef.current[currentKey] ?? [];
        const lastUserMessage = [...currentMessages].reverse().find(m => m.role === MESSAGE_ROLES.USER);
        if (!lastUserMessage) return;

        const userMessageIndex = currentMessages.findIndex(m => m.id === lastUserMessage.id);
        updateSessionMessages(currentKey, currentMessages.slice(0, userMessageIndex + 1));

        await handleSubmit(null, { content: lastUserMessage.content?.[0]?.data });
    }, [currentKey, handleSubmit, updateSessionMessages]);

    const stop = useCallback(async () => {
        const activeRun = activeRunsRef.current[currentKey];
        if (!activeRun) return;
        setStopError(null);
        try {
            await interactiveServiceFetch(`${api}/runs/${activeRun.runId}/cancel`, {
                method: 'POST',
            });
        } catch (err) {
            setStopError('Failed to interrupt query');
        }
    }, [api, currentKey]);

    const handleInputChange = useCallback(e => setInput(e.target.value), []);

    return {
        messages,
        input,
        setInput,
        handleSubmit,
        handleInputChange,
        isLoading,
        activeRunId,
        error,
        stopError,
        append,
        reload,
        stop,
        // Session management
        currentSessionId,
        sessions,
        isLoadingSessions,
        sessionsLoadError,
        loadingSessionId,
        sessionDeletedNotice,
        sessionDeleteError,
        sessionLoadError,
        clearCurrentSession,
        loadSession,
        deleteSession,
        updateSessionTitle,
        fetchSessions,
        // Multi-stream
        streamDescriptors,
        streamingSessionIds,
        handleStreamMessage,
        handleStreamError,
    };
};

export default useChat;
